import React, { useState, useEffect, useRef } from 'react';
import { SOCIAL_LINKS } from '../../config/constants';
import { useOnChainStats } from '../../hooks/useOnChainStats';

const CommunityStats: React.FC = () => {
    const onChainStats = useOnChainStats(undefined, 900_000); // refresh every 15min
    const [displayCounters, setDisplayCounters] = useState({
        holders: 0,
        txns: 0,
    });
    const hasAnimated = useRef(false);

    // Animated counter effect when data loads
    useEffect(() => {
        if (onChainStats.isLoading || hasAnimated.current) return;

        const targetHolders = onChainStats.holders;
        const targetTxns = onChainStats.totalTransfers;

        // Only animate if we have actual data
        if (targetHolders === 0 && targetTxns === 0) return;

        hasAnimated.current = true;
        const duration = 2000;
        const steps = 60;
        const interval = duration / steps;

        let step = 0;
        const timer = setInterval(() => {
            step++;
            const progress = Math.min(step / steps, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            setDisplayCounters({
                holders: Math.round(targetHolders * eased),
                txns: Math.round(targetTxns * eased),
            });

            if (step >= steps) clearInterval(timer);
        }, interval);

        return () => clearInterval(timer);
    }, [onChainStats.isLoading, onChainStats.holders, onChainStats.totalTransfers]);

    // When data updates after initial animation, update directly
    useEffect(() => {
        if (!hasAnimated.current || onChainStats.isLoading) return;
        setDisplayCounters({
            holders: onChainStats.holders,
            txns: onChainStats.totalTransfers,
        });
    }, [onChainStats.holders, onChainStats.totalTransfers, onChainStats.isLoading]);

    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return `${num}`;
    };

    const formatUsd = (num: number) => {
        if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
        if (num >= 1000) return `$${(num / 1000).toFixed(2)}K`;
        if (num > 0) return `$${num.toFixed(2)}`;
        return '$0';
    };

    const stats = [
        {
            label: 'Holders',
            value: onChainStats.isLoading ? '...' : formatNumber(displayCounters.holders),
            emoji: '🦆',
            live: true,
        },
        {
            label: 'Transactions',
            value: onChainStats.isLoading ? '...' : formatNumber(displayCounters.txns),
            emoji: '⚡',
            live: true,
        },
        {
            label: 'Volume 24h',
            value: onChainStats.isLoading ? '...' : formatUsd(onChainStats.volume24h),
            emoji: '📊',
            live: true,
        },
        {
            label: 'Market Cap',
            value: onChainStats.isLoading ? '...' : formatUsd(onChainStats.fdv),
            emoji: '💰',
            live: true,
        },
        {
            label: 'Network',
            value: 'Monad',
            emoji: '🟣',
            live: false,
        },
        {
            label: 'Rugs Pulled',
            value: '0',
            emoji: '🔒',
            live: false,
        },
    ];

    return (
        <section id="community" className="section community-section">
            <h2 className="section-title">🌍 Join the Pond</h2>
            <p className="community-subtitle">
                The $DUCK community is growing every day. Be part of something legendary.
            </p>

            {/* Stats Grid */}
            <div className="community-stats-grid">
                {stats.map((stat, index) => (
                    <div key={index} className="community-stat-card glass-card">
                        <div className="community-stat-emoji">{stat.emoji}</div>
                        <div className="community-stat-value">{stat.value}</div>
                        <div className="community-stat-label">
                            {stat.label}
                            {stat.live && (
                                <span className="community-live-dot" title="Live data" />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {onChainStats.lastUpdated && (
                <p className="community-updated">
                    Live data updated {new Date(onChainStats.lastUpdated).toLocaleTimeString()}
                    {onChainStats.error && <span style={{ color: '#f87171', marginLeft: '0.5rem' }}>(partial data)</span>}
                </p>
            )}

            {/* Social Links */}
            <div className="community-socials">
                <a
                    href={SOCIAL_LINKS.twitter}
                    target="_blank"
                    rel="noreferrer"
                    className="community-social-card glass-card"
                >
                    <div className="social-card-icon">𝕏</div>
                    <div className="social-card-info">
                        <h3>Follow on X</h3>
                        <p>Get the latest quacks</p>
                    </div>
                    <span className="social-card-arrow">→</span>
                </a>

                <a
                    href={SOCIAL_LINKS.telegram}
                    target="_blank"
                    rel="noreferrer"
                    className="community-social-card glass-card"
                >
                    <div className="social-card-icon">✈️</div>
                    <div className="social-card-info">
                        <h3>Join Telegram</h3>
                        <p>Chat with fellow ducks</p>
                    </div>
                    <span className="social-card-arrow">→</span>
                </a>

                <a
                    href={SOCIAL_LINKS.nadfun}
                    target="_blank"
                    rel="noreferrer"
                    className="community-social-card glass-card"
                >
                    <div className="social-card-icon">🏊</div>
                    <div className="social-card-info">
                        <h3>Trade on nad.fun</h3>
                        <p>Buy & sell $DUCK</p>
                    </div>
                    <span className="social-card-arrow">→</span>
                </a>
            </div>
        </section>
    );
};

export default CommunityStats;
